import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const CTAButton = styled.a`
  background: var(--gradient-primary);
  color: var(--surface);

  padding: 15px 35px;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-decoration: none;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  box-shadow: var(--shadow-soft);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const Hero = styled.section`
  position: relative;
  /* svh = vidljiva visina ekrana na mobilnom (bez URL trake) —
     sa 100vh je scroll indikator na dnu bio ispod ivice ekrana */
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  align-items: center;
  text-align: center;
  overflow: hidden;
  flex-direction: column;
`;

const kenBurns = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.07);
  }
`;

const softFade = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const chevronCascade = keyframes`
  0% {
    opacity: 0;
    transform: rotate(45deg) translate(-3px, -3px);
  }
  40% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: rotate(45deg) translate(3px, 3px);
  }
`;

export const HeroVideo = styled.video`
  width: 100%;
  height: 100vh;
  object-fit: cover;
  opacity: 1;
  z-index: 1;
  transform-origin: center;
  animation: ${kenBurns} 26s ease-in-out infinite alternate;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;

  background:
    radial-gradient(
      ellipse 60% 45% at center,
      rgba(20, 5, 10, 0.32) 0%,
      rgba(20, 5, 10, 0) 70%
    ),
    radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 30%,
      rgba(0, 0, 0, 0.35) 100%
    ),
    linear-gradient(
      180deg,
      rgba(254, 252, 249, 0) 0%,
      rgba(90, 14, 27, 0.5) 100%
    );
`;

export const ScrollChevrons = styled.button`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.3s ease;
  animation: ${softFade} 1.6s ease-out 1s both;

  &:hover {
    opacity: 1;
  }

  span {
    width: 12px;
    height: 12px;
    border-right: 1.5px solid rgba(255, 255, 255, 0.9);
    border-bottom: 1.5px solid rgba(255, 255, 255, 0.9);
    transform: rotate(45deg);
    animation: ${chevronCascade} 1.8s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.25s;
    margin-top: -7px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    span {
      animation: none;
      opacity: 0.7;
    }
  }
`;

export const HeroCenter = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

export const Logo = styled.img`
  max-height: 200px;
  animation: ${softFade} 1.6s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: 1440px) {
    max-height: 150px;
  }
  @media (max-width: 450px) {
    max-height: 130px;
  }
`;

export const HeroTagline = styled.p`
  margin-top: 34px;
  color: rgba(255, 255, 255, 0.92);
  font-family: "Source Code Pro", monospace;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 5px;
  text-transform: uppercase;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 16px;
  animation: ${softFade} 1.6s ease-out 0.5s both;

  &::before,
  &::after {
    content: "";
    width: 38px;
    height: 1px;
    background: rgba(255, 255, 255, 0.55);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: 450px) {
    font-size: 10px;
    letter-spacing: 3px;
    margin-top: 22px;
    gap: 10px;

    &::before,
    &::after {
      width: 22px;
    }
  }
`;

export const Relative = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100svh;
`;
export const Section = styled.section`
  padding: ${(props) => (props.$paddingTop ? "120px 0" : "40px 0px")};
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;

  h2 {
    font-size: 48px;
    line-height: 60px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }
`;

export const GallerySection = styled(Section)`
  background: var(--surface);
`;

export const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 25px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const GridItem = styled.div`
  aspect-ratio: 1;
  display: inline;
  background: var(--secondary);
  border-radius: 20px;
  overflow: hidden;
  max-width: 320px;
  width: 100%;
  position: relative;
  transition: all 0.4s ease;
  border: 2px solid var(--accent-light);
  cursor: pointer;
  animation: ${(props) => (props.$animate ? fadeInUp : "none")} 0.6s ease
    forwards;
  opacity: ${(props) => (props.$animate ? 1 : 0)};

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: var(--shadow-medium);
    border-color: var(--accent);
    background: var(--secondary);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--gradient-primary);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover::before {
    opacity: 0.1;
  }
`;

export const GridContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px;
  background: var(--gradient-primary);
  color: var(--surface);
  z-index: 2;

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  p {
    font-size: 14px;
    line-height: 1.4;
    opacity: 0.9;
  }
`;

export const CoursesSection = styled(Section)`
  background: var(--background);
  /* Mini edukacije su sekcija iznad — uklanjamo gornji padding da razmak
     do naslova "Dostupni kursevi" ne bude duplo veliki (mini bottom + ovaj top) */
  padding-top: 0;
`;

export const CoursesGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  min-height: 835.36px;
  align-items: center;
  gap: 2rem;

  /* Od laptop širine naviše drži tačno 3 kartice u redu — na 13" laptopu
     je inače flex-basis (420px) bio preširok pa se lomilo na 2 pa 1.
     max-width: 420px zadržava izgled na velikim ekranima. */
  @media (min-width: 992px) {
    & > * {
      flex: 1 1 calc((100% - 4rem) / 3);
      max-width: 420px;
      min-width: 0;
    }
  }
`;

export const CourseContent = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const CourseBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  padding: 6px 16px;
  background: var(--gradient-primary);
  color: var(--surface);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: "Source Code Pro", monospace;
`;

export const CourseTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
  margin: 0;
`;

export const CourseStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-top: 1px solid var(--accent-light);
  border-bottom: 1px solid var(--accent-light);
`;

export const CourseStat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 14px;

  svg {
    color: var(--primary);
    font-size: 16px;
    min-width: 16px;
  }

  span {
    line-height: 1.4;
  }
`;

export const CourseCard = styled.div`
  background: var(--surface);
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  position: relative;
  transition: all 0.4s ease;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  box-shadow: var(--shadow-soft);
  animation: ${(props) => (props.$animate ? scaleIn : "none")} 0.6s ease
    forwards;
  opacity: ${(props) => (props.$animate ? 1 : 0)};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-medium);
    border-color: var(--primary);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const CourseImage = styled.div`
  width: 100%;
  height: 240px;
  background-image: url("https://lukaya.art/wp-content/uploads/2025/07/Shotcut_00_00_25_833-1-scaled.jpg");
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(to top, var(--surface), transparent);
  }
`;

export const CourseDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 15px;
  margin: 0;
`;

export const CoursePrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 10px 0;

  .original-price {
    text-decoration: line-through;
    color: var(--text-muted);
    font-size: 18px;
    font-weight: 500;
  }

  .current-price {
    font-size: 36px;
    font-weight: 800;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-family: "Source Code Pro", monospace;
  }
`;

export const CourseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

export const CourseIcon = styled.div`
  width: 55px;
  height: 55px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--surface);
`;

export const CourseFeatures = styled.ul`
  list-style: none;
  margin: 25px 0;

  li {
    padding: 12px 0;
    padding-left: 35px;
    position: relative;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--accent-light);

    &:last-child {
      border-bottom: none;
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="%23a8d8a8"/></svg>')
        no-repeat center;
      background-size: contain;
    }
  }
`;

export const FullWidthCTA = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--surface);

  border: none;
  padding: 20px;
  border-radius: 30px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  box-shadow: var(--shadow-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 1px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.added {
    background: var(--success);
    color: var(--surface);

    &:hover {
      background: var(--success);
      color: var(--surface);
      transform: translateY(-3px);
    }
  }
`;
export const StorySection = styled(Section)`
  background: var(--surface);
`;

export const StoryContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  align-items: center;
  max-height: 100dvh;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 1050px) {
    flex-direction: column;
    max-height: 100%;
  }
`;

export const StoryText = styled.div`
  animation: ${slideInLeft} 1s ease-out;

  h3 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 30px;
    text-transform: uppercase;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: 25px;
  }

  > div {
    margin-top: 40px;
  }

  .highlight {
    font-weight: 600;
    color: var(--accent);
  }
`;

export const StoryImage = styled.img`
  background: var(--surface);
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: none;
  animation: ${slideInLeft} 1s ease-out 0.3s both;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--gradient-primary);
    opacity: 0.05;
  }
`;

export const ImageCaption = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  text-align: center;
  color: var(--text-muted);
`;

export const BenefitsSection = styled(Section)`
  background: var(--background);
`;

export const BenefitsGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
`;

export const BenefitCard = styled.div`
  background: var(--surface);
  padding: 45px 35px;
  border-radius: 25px;
  max-width: 300px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid var(--accent-light);
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-soft);
  animation: ${(props) => (props.$animate ? fadeInUp : "none")} 0.6s ease
    forwards;
  opacity: ${(props) => (props.$animate ? 1 : 0)};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: var(--gradient-primary);
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-medium);
    border-color: var(--accent);
    background: var(--surface);
  }

  h4 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 15px;
    text-transform: uppercase;
    color: var(--text-primary);
  }

  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

export const BenefitIcon = styled.div`
  width: 75px;
  height: 75px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  font-size: 32px;
  color: var(--surface);
`;

export const CenterText = styled.div`
  text-align: center;
  margin-top: 60px;
`;

export const Container = styled.div`
  max-width: 1400px;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
